<template>
  <Teleport to="body">
    <Transition name="fade-scale">
      <div
        v-if="isOpen"
        ref="modalRef"
        class="fixed inset-0 z-90 flex flex-col bg-black"
        role="dialog"
        aria-modal="true"
        :aria-label="t('common.close.nav.modal')"
        @click="emit('close')"
      >
        <button
          @click.stop="emit('close')"
          class="absolute top-5 right-5 z-50 cursor-pointer text-white md:fixed lg:top-7.5 lg:right-7.5"
          :aria-label="t('common.close.nav.modal')"
        >
          <XmarkIcon class="h-4.5 w-4.5" />
        </button>

        <div
          class="fixed top-0 left-1/2 z-10 hidden -translate-x-1/2 py-5 md:flex"
          @click.stop
        >
          <ul
            class="flex max-w-full items-center justify-between gap-8 rounded-[7px] bg-black px-3.75 py-3 text-white"
          >
            <li v-for="cat in menuItems" :key="cat.id">
              <component
                :is="hasChildren(cat) ? 'button' : NuxtLink"
                :to="hasChildren(cat) ? undefined : menuItemHref(cat)"
                :target="
                  !hasChildren(cat) && cat.type === 'external'
                    ? '_blank'
                    : '_self'
                "
                class="group flex w-max cursor-pointer flex-col items-center text-sm leading-[115%] xl:text-base/[119%] 2xl:text-[18.6px]/[124%]"
                @click.stop="
                  hasChildren(cat)
                    ? emit('change-category', cat.id)
                    : emit('close')
                "
              >
                <span>{{ cat.label }}</span>
                <div
                  :class="[
                    'mx-auto h-px bg-white transition-all duration-100 ease-in-out',
                    activeCategory === cat.id
                      ? 'w-full'
                      : 'w-0 group-hover:w-full',
                  ]"
                />
              </component>
            </li>
          </ul>
        </div>

        <div
          class="absolute top-5 left-5 flex items-center gap-7.5 text-white lg:hidden"
        >
          <button
            @click.stop="openSearchModal"
            class="h-4.75 w-4.75"
            :aria-label="t('common.open.search.modal')"
          >
            <LoopIcon />
          </button>

          <button
            @click.stop="changeLocale"
            class="cursor-pointer text-base leading-[113%] uppercase"
          >
            {{ locale === 'cs' ? 'En' : 'Cs' }}
          </button>
        </div>

        <div
          class="hidden-scrollbar mt-24 mb-6 flex w-full shrink-0 justify-between gap-6 overflow-x-auto px-5 pb-2 text-base leading-[113%] text-white select-none md:hidden"
        >
          <component
            :is="hasChildren(cat) ? 'button' : NuxtLink"
            v-for="cat in menuItems"
            :key="cat.id"
            :to="hasChildren(cat) ? undefined : menuItemHref(cat)"
            :target="
              !hasChildren(cat) && cat.type === 'external' ? '_blank' : '_self'
            "
            class="w-max min-w-max cursor-pointer text-center"
            @click.stop="
              hasChildren(cat) ? emit('change-category', cat.id) : emit('close')
            "
          >
            <span
              :class="[
                'text-sm transition-colors duration-100',
                activeCategory === cat.id
                  ? 'text-white underline decoration-1 underline-offset-5'
                  : 'text-white',
              ]"
            >
              {{ cat.label }}
            </span>
          </component>
        </div>

        <div
          class="hidden-scrollbar bp1680:w-[590px] bp1680:text-[23px]/[29px] mx-auto flex min-h-0 w-full flex-1 flex-col overflow-x-hidden overflow-y-auto px-5 pb-24 text-[16px]/[19px] text-white md:w-[506px] md:max-w-none md:px-0 md:pt-37.5 md:pb-16 md:text-[17px]/[21px] lg:pt-42.5 xl:text-[20px]/[24px] 2xl:w-[590px] 2xl:text-[23px]/[29px]"
          @click.stop
          @scroll="handleScroll"
        >
          <div class="flex flex-1 flex-col justify-between">
            <Transition name="page-fade" mode="out-in">
              <div :key="activeCategory" class="flex w-full flex-1 flex-col">
                <div class="flex min-h-60 flex-col">
                  <NavigationNode
                    v-for="item in categoryData?.children"
                    :key="item.id"
                    :node="item"
                    :page-type="item.type"
                    @close="emit('close')"
                  />
                </div>

                <NuxtLink
                  v-if="categoryData?.cta && getCtaTitle(categoryData.cta)"
                  :to="getCtaHref(categoryData.cta)"
                  :target="
                    categoryData.cta.type === 'external'
                      ? '_blank'
                      : categoryData.cta.target || '_self'
                  "
                  :rel="
                    categoryData.cta.type === 'external'
                      ? 'noopener noreferrer'
                      : undefined
                  "
                  class="group mt-15 flex items-center justify-between border border-white p-2.5 text-white transition-colors duration-300 hover:text-gray md:mt-17.5 2xl:mt-20"
                  @click="emit('close')"
                >
                  <span
                    class="text-base/[19px] font-normal md:text-[17px]/[21px] xl:text-xl/6"
                  >
                    {{ getCtaTitle(categoryData.cta) }}
                  </span>
                  <ArrowIcon
                    class="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1"
                  />
                </NuxtLink>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { NuxtLink } from '#components'
import type { CtaButton, MenuItem } from '~/types'

interface Props {
  isOpen: boolean
  activeCategory: string
  menuItems: MenuItem[]
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'change-category', 'scroll', 'open-search'])

const { t, locale } = useI18n()
const localePath = useLocalePath()
const modalRef = ref<HTMLElement | null>(null)
const switchLocalePath = useSwitchLocalePath()

useFocusTrap(modalRef, toRef(props, 'isOpen'), () => emit('close'))

const changeLocale = () => {
  const newLocale = locale.value === 'cs' ? 'en' : 'cs'
  window.location.replace(switchLocalePath(newLocale))
}

const menuItemHref = (item: MenuItem) => {
  const href = item.url || item.link
  if (!href) return null
  if (item.type === 'external') return href
  return localePath(href)
}

const getCtaTitle = (cta: CtaButton | null | undefined) => {
  if (!cta) return ''
  const rawTitle =
    cta.title || cta.label || cta.linkable?.title || cta.linkable?.name
  if (!rawTitle) return ''
  if (typeof rawTitle === 'object') {
    return rawTitle[locale.value] || rawTitle.cs || rawTitle.en || ''
  }
  return rawTitle
}

const getCtaHref = (cta: CtaButton | null | undefined) => {
  if (!cta) return ''
  const rawUrl = cta.url || cta.link || cta.linkable?.menu_url
  if (!rawUrl) return '#'
  if (
    cta.type === 'external' ||
    rawUrl.startsWith('http://') ||
    rawUrl.startsWith('https://')
  ) {
    return rawUrl
  }
  return localePath(rawUrl)
}

const categoryData = computed(() => {
  return props.menuItems.find((cat) => cat.id === props.activeCategory)
})

const hasChildren = (item: MenuItem) =>
  !!item.children && item.children.length > 0

const handleScroll = (e: Event) => {
  const target = e.target as HTMLElement
  emit('scroll', target.scrollTop)
}

const openSearchModal = () => {
  emit('open-search')
  emit('close')
}

watch(
  () => props.isOpen,
  (newVal) => {
    if (import.meta.server) return
    const header = document.querySelector('header')
    if (newVal) {
      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'
      header?.setAttribute('inert', '')
    } else {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      header?.removeAttribute('inert')
      emit('scroll', 0)
    }
  },
)

onUnmounted(() => {
  if (import.meta.server) return
  document.documentElement.style.overflow = ''
  document.body.style.overflow = ''
  document.querySelector('header')?.removeAttribute('inert')
})
</script>

<style scoped>
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: opacity 0.2s ease-in-out;
}

.fade-scale-enter-from,
.fade-scale-leave-to {
  opacity: 0;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition:
    opacity 0.15s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.15s cubic-bezier(0.16, 1, 0.3, 1);
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
